"use client";

import { c as _c } from "react/compiler-runtime";
import type { OnDragEndResponder } from "@hello-pangea/dnd";
import type { StackProps } from "@mui/material";
import React, { useEffect, useState } from "react";
import { SolutionModal } from "#/features/codeRunner/ui/SolutionModal";
import { selectIsEditable } from "#/features/project/model/projectSlice";
import { DraggableSelectBarList } from "#/features/selectBar/ui/DraggableSelectBarList";
import { DraggableSelectBarChip } from "#/features/selectBar/ui/SelectBarChip";
import type { UseTRPCQueryResult } from "#/server/api/trpc";
import type { PlaygroundSolution } from "#/server/db/generated/browser";
import { api } from "#/shared/api";
import type { RouterOutputs } from "#/shared/api";
import { usePlaygroundSlugs } from "#/shared/hooks";
import { useI18nContext } from "#/shared/hooks";
import { useAppDispatch, useAppSelector } from "#/store/hooks";
type SolutionBrief = Pick<PlaygroundSolution, "id" | "slug" | "title" | "order">;
type SolutionSelectBarProps = StackProps & {
  selectedProject: UseTRPCQueryResult<RouterOutputs["project"]["getBySlug"]>;
};
export const SolutionSelectBar: React.FC<SolutionSelectBarProps> = t0 => {
  const $ = _c(71);
  let restProps;
  let selectedProject;
  if ($[0] !== t0) {
    ({
      selectedProject,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = restProps;
    $[2] = selectedProject;
  } else {
    restProps = $[1];
    selectedProject = $[2];
  }
  const {
    LL
  } = useI18nContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    projectSlug: t1,
    caseSlug: t2,
    solutionSlug: t3,
    setSolution
  } = usePlaygroundSlugs();
  const projectSlug = t1 === undefined ? "" : t1;
  const caseSlug = t2 === undefined ? "" : t2;
  const solutionSlug = t3 === undefined ? "" : t3;
  const solutions = selectedProject.data?.solutions;
  const dispatch = useAppDispatch();
  const trpcUtils = api.useUtils();
  let t4;
  if ($[3] !== projectSlug || $[4] !== trpcUtils.project.getBySlug) {
    t4 = solutions_0 => {
      trpcUtils.project.getBySlug.setData(projectSlug, prevData => {
        if (!prevData) {
          return prevData;
        }
        return {
          ...prevData,
          solutions: solutions_0
        };
      });
    };
    $[3] = projectSlug;
    $[4] = trpcUtils.project.getBySlug;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const updateSolutionsCache = t4;
  let t5;
  if ($[6] !== projectSlug || $[7] !== selectedProject.data?.id || $[8] !== solutionSlug || $[9] !== trpcUtils.project.getBySlug || $[10] !== trpcUtils.project.getSolutionBySlug) {
    t5 = async slug => {
      await trpcUtils.project.getBySlug.invalidate(projectSlug);
      await trpcUtils.project.getSolutionBySlug.invalidate({
        projectId: selectedProject.data?.id || "",
        slug: slug || solutionSlug
      });
    };
    $[6] = projectSlug;
    $[7] = selectedProject.data?.id;
    $[8] = solutionSlug;
    $[9] = trpcUtils.project.getBySlug;
    $[10] = trpcUtils.project.getSolutionBySlug;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const invalidateQueries = t5;
  let t6;
  if ($[12] !== invalidateQueries || $[13] !== setSolution) {
    t6 = {
      onSuccess: async data => {
        await invalidateQueries(data.slug);
        setSolution(data.slug);
      }
    };
    $[12] = invalidateQueries;
    $[13] = setSolution;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const addSolution = api.project.addSolution.useMutation(t6);
  let t7;
  if ($[15] !== updateSolutionsCache) {
    t7 = {
      onSuccess: async newList => {
        updateSolutionsCache(newList);
      }
    };
    $[15] = updateSolutionsCache;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const reorderSolutions = api.project.reorderSolutions.useMutation(t7);
  let t8;
  if ($[17] !== invalidateQueries || $[18] !== setSolution || $[19] !== solutionSlug) {
    t8 = {
      onSuccess: async data_0 => {
        await invalidateQueries();
        if (solutionSlug === data_0.slug) {
          setSolution("");
        }
      }
    };
    $[17] = invalidateQueries;
    $[18] = setSolution;
    $[19] = solutionSlug;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const deleteSolution = api.project.deleteSolution.useMutation(t8);
  const isLoading = selectedProject.isLoading || addSolution.isPending || deleteSolution.isPending;
  const isEditable = useAppSelector(selectIsEditable);
  let t9;
  if ($[21] !== caseSlug || $[22] !== selectedProject.data || $[23] !== setSolution || $[24] !== solutionSlug) {
    t9 = () => {
      if (solutionSlug || !selectedProject.data || !caseSlug) {
        return;
      }
      const firstSolutionSlug = selectedProject.data.solutions[0]?.slug;
      if (firstSolutionSlug) {
        setSolution(firstSolutionSlug);
      }
    };
    $[21] = caseSlug;
    $[22] = selectedProject.data;
    $[23] = setSolution;
    $[24] = solutionSlug;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== caseSlug || $[27] !== dispatch || $[28] !== selectedProject.data || $[29] !== setSolution || $[30] !== solutionSlug) {
    t10 = [solutionSlug, selectedProject.data, dispatch, setSolution, caseSlug];
    $[26] = caseSlug;
    $[27] = dispatch;
    $[28] = selectedProject.data;
    $[29] = setSolution;
    $[30] = solutionSlug;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  useEffect(t9, t10);
  let t11;
  if ($[32] !== setSolution) {
    t11 = solution => {
      setSolution(solution.slug);
    };
    $[32] = setSolution;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const handleSolutionClick = t11;
  let t12;
  if ($[34] !== addSolution || $[35] !== selectedProject.data || $[36] !== solutionSlug || $[37] !== solutions?.length) {
    t12 = () => {
      if (!selectedProject.data) {
        return;
      }
      const order = solutions?.length ?? 0;
      addSolution.mutate({
        projectId: selectedProject.data.id,
        order,
        referenceSolutionSlug: solutionSlug
      });
    };
    $[34] = addSolution;
    $[35] = selectedProject.data;
    $[36] = solutionSlug;
    $[37] = solutions?.length;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const handleAddSolution = t12;
  let t13;
  if ($[39] !== handleSolutionClick) {
    t13 = solution_0 => {
      handleSolutionClick(solution_0);
      setIsModalOpen(true);
    };
    $[39] = handleSolutionClick;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const handleSolutionEdit = t13;
  let t14;
  if ($[41] !== reorderSolutions || $[42] !== selectedProject.data || $[43] !== solutions || $[44] !== updateSolutionsCache) {
    t14 = result => {
      if (!result.destination || result.destination.index === result.source.index || !solutions || !selectedProject.data) {
        return;
      }
      const {
        source,
        destination
      } = result;
      const newList_0 = Array.from(solutions);
      const removed = newList_0.splice(source.index, 1);
      newList_0.splice(destination.index, 0, ...removed);
      updateSolutionsCache(newList_0);
      reorderSolutions.mutate({
        projectId: selectedProject.data.id,
        solutionIds: newList_0.map(_temp)
      });
    };
    $[41] = reorderSolutions;
    $[42] = selectedProject.data;
    $[43] = solutions;
    $[44] = updateSolutionsCache;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const onItemDragEnd = t14;
  let t15;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsModalOpen(false);
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== isModalOpen) {
    t16 = <SolutionModal open={isModalOpen} onClose={t15} />;
    $[47] = isModalOpen;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const t17 = !selectedProject.data;
  let t18;
  if ($[49] !== LL) {
    t18 = LL.ADD_NEW_SOLUTION();
    $[49] = LL;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== LL || $[52] !== handleSolutionClick || $[53] !== handleSolutionEdit || $[54] !== isEditable || $[55] !== isLoading || $[56] !== solutionSlug || $[57] !== solutions) {
    t19 = (_, droppableSnapshot) => solutions?.map((solution_2, index) => <DraggableSelectBarChip id={solution_2.id} key={solution_2.id} index={index} droppableSnapshot={droppableSnapshot} editLabel={LL.EDIT_SOLUTION()} isCurrent={solution_2.slug === solutionSlug} isEditable={isEditable} label={solution_2.title} disabled={isLoading} onClick={() => handleSolutionClick(solution_2)} onEditClick={() => handleSolutionEdit(solution_2)} />);
    $[51] = LL;
    $[52] = handleSolutionClick;
    $[53] = handleSolutionEdit;
    $[54] = isEditable;
    $[55] = isLoading;
    $[56] = solutionSlug;
    $[57] = solutions;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== handleAddSolution || $[60] !== isEditable || $[61] !== isLoading || $[62] !== onItemDragEnd || $[63] !== restProps || $[64] !== t17 || $[65] !== t18 || $[66] !== t19) {
    t20 = <DraggableSelectBarList droppableId="droppable-solutions" isLoading={isLoading} isEmpty={t17} onItemDragEnd={onItemDragEnd} addItemTitle={t18} handleAddItem={handleAddSolution} isEditable={isEditable} {...restProps}>{t19}</DraggableSelectBarList>;
    $[59] = handleAddSolution;
    $[60] = isEditable;
    $[61] = isLoading;
    $[62] = onItemDragEnd;
    $[63] = restProps;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== t16 || $[69] !== t20) {
    t21 = <>{t16}{t20}</>;
    $[68] = t16;
    $[69] = t20;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  return t21;
};
function _temp(solution_1) {
  return solution_1.id;
}